import React, { useState, useEffect } from 'react';
import Loader from '../../components/Loader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAnnouncement = () => {
  const [announcement, setAnnouncement] = useState({
    title: '',
    description: '',
    buttonText: '',
    buttonLink: '',
    backgroundColor: '#0d9488',
    textColor: '#ffffff',
    active: false,
    startDate: '',
    endDate: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchAnnouncement();
  }, []);

  const fetchAnnouncement = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/announcement`);
      const data = await response.json();
      setAnnouncement({
        ...data,
        startDate: data.startDate ? data.startDate.split('T')[0] : '',
        endDate: data.endDate ? data.endDate.split('T')[0] : ''
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching announcement:', error);
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAnnouncement(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const payload = {
        ...announcement,
        startDate: announcement.startDate || null,
        endDate: announcement.endDate || null
      };

      const response = await fetch(`${API_URL}/api/admin/announcement`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setMessage('Announcement updated successfully!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Error updating announcement');
      }
    } catch (error) {
      console.error('Error saving announcement:', error);
      setMessage('Error updating announcement');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Manage Announcement Banner</h1>
        <p>Control the promotional banner displayed after the hero section</p>
      </div>

      <form onSubmit={handleSubmit} className="admin-form">
        <div className="form-section">
          <h2>Banner Content</h2>
          
          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={announcement.title}
              onChange={handleChange}
              required
              placeholder="e.g., 🎉 Special Offer: Dubai Package at 30% OFF!"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              name="description"
              value={announcement.description}
              onChange={handleChange}
              required
              rows="3"
              placeholder="Detailed description of the offer or announcement"
            />
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="buttonText">Button Text</label>
              <input
                type="text"
                id="buttonText"
                name="buttonText"
                value={announcement.buttonText}
                onChange={handleChange}
                placeholder="e.g., Book Now"
              />
            </div>

            <div className="form-group">
              <label htmlFor="buttonLink">Button Link</label>
              <input
                type="text"
                id="buttonLink"
                name="buttonLink"
                value={announcement.buttonLink}
                onChange={handleChange}
                placeholder="/destinations or https://example.com"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Styling</h2>
          
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="backgroundColor">Background Color</label>
              <div className="color-input-wrapper">
                <input
                  type="color"
                  id="backgroundColor"
                  name="backgroundColor"
                  value={announcement.backgroundColor}
                  onChange={handleChange}
                />
                <input
                  type="text"
                  value={announcement.backgroundColor}
                  onChange={(e) => setAnnouncement(prev => ({ ...prev, backgroundColor: e.target.value }))}
                  placeholder="#0d9488"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="textColor">Text Color</label>
              <div className="color-input-wrapper">
                <input
                  type="color"
                  id="textColor"
                  name="textColor"
                  value={announcement.textColor}
                  onChange={handleChange}
                />
                <input
                  type="text"
                  value={announcement.textColor}
                  onChange={(e) => setAnnouncement(prev => ({ ...prev, textColor: e.target.value }))}
                  placeholder="#ffffff"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Schedule</h2>
          
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="startDate">Start Date (Optional)</label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={announcement.startDate}
                onChange={handleChange}
              />
              <small>Leave empty to show immediately</small>
            </div>

            <div className="form-group">
              <label htmlFor="endDate">End Date (Optional)</label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={announcement.endDate}
                onChange={handleChange}
              />
              <small>Leave empty for no expiration</small>
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="active"
                checked={announcement.active}
                onChange={handleChange}
              />
              <span>Active (Show banner on website)</span>
            </label>
          </div>
        </div>

        {message && (
          <div className={`admin-message ${message.includes('Error') ? 'error' : 'success'}`}>
            {message}
          </div>
        )}

        <div className="form-actions">
          <button type="submit" className="admin-btn primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>

      {announcement.active && (
        <div className="preview-section">
          <h2>Preview</h2>
          <div 
            className="announcement-banner"
            style={{
              backgroundColor: announcement.backgroundColor,
              color: announcement.textColor
            }}
          >
            <div className="announcement-content">
              <div className="announcement-text">
                <h3 className="announcement-title">{announcement.title || 'Title'}</h3>
                <p className="announcement-description">{announcement.description || 'Description'}</p>
              </div>
              
              {announcement.buttonText && (
                <button 
                  className="announcement-button"
                  style={{
                    borderColor: announcement.textColor,
                    color: announcement.textColor
                  }}
                >
                  {announcement.buttonText}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAnnouncement;
